import { useEffect, useState } from "react";
import { ShieldAlert, Loader2, Ban, CheckCircle2, Trash2, RefreshCw } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { PLATFORM_ADMIN as PA } from "@/constants/testIds";

const TOKEN_STORAGE_KEY = "platform_admin_token";

function platformHeaders(token) {
  return { headers: { "X-Platform-Token": token } };
}

function formatDate(iso) {
  if (!iso) return "-";
  try {
    return new Date(iso).toLocaleDateString("es-AR", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return iso;
  }
}

function TokenGate({ onConnected }) {
  const [tokenInput, setTokenInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const connect = async () => {
    setError("");
    setLoading(true);
    try {
      await api.get("/platform/clinics", platformHeaders(tokenInput));
      sessionStorage.setItem(TOKEN_STORAGE_KEY, tokenInput);
      onConnected(tokenInput);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Token invalido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#1C1917] px-4" data-testid={PA.page}>
      <div className="w-full max-w-sm bg-[#262220] border border-white/10 rounded-2xl p-8 space-y-5 shadow-2xl">
        <div className="flex flex-col items-center gap-2 text-center">
          <div className="w-12 h-12 rounded-full bg-[#8C7D70]/20 flex items-center justify-center">
            <ShieldAlert className="w-6 h-6 text-[#C9B8A6]" />
          </div>
          <h1 className="font-serif text-xl text-white">Administración de plataforma</h1>
          <p className="text-sm text-white/50">Acceso restringido. Ingresa el token de plataforma.</p>
        </div>
        <div>
          <Label className="text-xs text-white/60 mb-1 block">Token de plataforma</Label>
          <Input
            type="password"
            value={tokenInput}
            onChange={(e) => setTokenInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && connect()}
            className="bg-[#1C1917] border-white/10 text-white"
            placeholder="••••••••••••••••"
            data-testid={PA.tokenInput}
          />
        </div>
        {error && <p className="text-sm text-red-400" data-testid={PA.tokenError}>{error}</p>}
        <Button
          onClick={connect}
          disabled={loading || !tokenInput}
          className="w-full bg-[#8C7D70] hover:bg-[#736357] text-white"
          data-testid={PA.connectButton}
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ingresar"}
        </Button>
      </div>
    </div>
  );
}

function DeleteClinicDialog({ clinic, token, open, onOpenChange, onDeleted }) {
  const [confirmName, setConfirmName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (open) setConfirmName("");
  }, [open]);

  const handleDelete = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.delete(`/platform/clinics/${clinic.id}`, {
        ...platformHeaders(token),
        data: { confirm_name: confirmName },
      });
      const countsSummary = Object.entries(data.deleted_counts || {}).map(([k, v]) => `${k}: ${v}`).join(", ") || "sin datos adicionales";
      toast({
        title: "Clínica eliminada",
        description: `${data.message} Respaldo guardado en el servidor. Eliminado: ${countsSummary}`,
      });
      onOpenChange(false);
      onDeleted();
    } catch (err) {
      toast({ title: "No se pudo eliminar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  if (!clinic) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#262220] border-white/10 text-white" data-testid={PA.deleteDialog}>
        <DialogHeader>
          <DialogTitle className="text-red-400">Eliminar clínica permanentemente</DialogTitle>
          <DialogDescription className="text-white/60">
            Esta acción borra <strong>{clinic.name}</strong> y TODOS sus datos (usuarios, pacientes, turnos,
            servicios, pagos, conversaciones, etc.) en cascada. Se genera un respaldo JSON antes de borrar,
            pero la acción no se puede deshacer desde esta pantalla. Escribe el nombre exacto para confirmar:
          </DialogDescription>
        </DialogHeader>
        <Input
          value={confirmName}
          onChange={(e) => setConfirmName(e.target.value)}
          placeholder={clinic.name}
          className="bg-[#1C1917] border-white/10 text-white"
          data-testid={PA.deleteConfirmInput}
        />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid={PA.deleteCancelButton}>
            Cancelar
          </Button>
          <Button
            variant="destructive"
            disabled={confirmName !== clinic.name || submitting}
            onClick={handleDelete}
            data-testid={PA.deleteConfirmButton}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Eliminar definitivamente"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ClinicsPanel({ token, onLogout }) {
  const [clinics, setClinics] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { toast } = useToast();

  const load = async () => {
    try {
      const [clinicsRes, auditRes] = await Promise.all([
        api.get("/platform/clinics", platformHeaders(token)),
        api.get("/platform/audit-log", platformHeaders(token)),
      ]);
      setClinics(clinicsRes.data);
      setAuditLog(auditRes.data);
    } catch (err) {
      if (err.response?.status === 403) {
        onLogout();
        return;
      }
      toast({ title: "Error al cargar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleStatus = async (clinic) => {
    setBusyId(clinic.id);
    try {
      await api.patch(`/platform/clinics/${clinic.id}/status`, { is_active: !clinic.is_active }, platformHeaders(token));
      toast({ title: clinic.is_active ? "Clínica desactivada" : "Clínica reactivada" });
      await load();
    } catch (err) {
      toast({ title: "No se pudo actualizar", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#1C1917] text-white px-4 sm:px-8 py-10" data-testid={PA.page}>
      <div className="max-w-5xl mx-auto space-y-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-serif text-2xl sm:text-3xl">Administración de plataforma</h1>
            <p className="text-sm text-white/50 mt-1">Listar, desactivar y eliminar clínicas (tenants). Toda acción queda auditada.</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="icon" onClick={load} className="border-white/10 text-white hover:bg-white/10">
              <RefreshCw className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={onLogout} className="border-white/10 text-white hover:bg-white/10" data-testid={PA.logoutButton}>
              Salir
            </Button>
          </div>
        </div>

        {!clinics ? (
          <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-white/50" /></div>
        ) : (
          <div className="bg-[#262220] border border-white/10 rounded-xl overflow-hidden">
            <Table data-testid={PA.clinicsTable}>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-white/60">Clínica</TableHead>
                  <TableHead className="text-white/60">Slug</TableHead>
                  <TableHead className="text-white/60">Estado</TableHead>
                  <TableHead className="text-white/60">Usuarios</TableHead>
                  <TableHead className="text-white/60">Servicios</TableHead>
                  <TableHead className="text-white/60">Pacientes</TableHead>
                  <TableHead className="text-white/60">Turnos</TableHead>
                  <TableHead className="text-white/60">Creada</TableHead>
                  <TableHead className="text-white/60 text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {clinics.map((c) => (
                  <TableRow key={c.id} className="border-white/10 hover:bg-white/5" data-testid={`${PA.clinicRowPrefix}${c.id}`}>
                    <TableCell className="font-medium">
                      {c.name}
                      <div className="text-xs text-white/40">{c.email}</div>
                    </TableCell>
                    <TableCell className="text-white/70">{c.slug || "-"}</TableCell>
                    <TableCell>
                      <Badge
                        variant={c.is_active ? "default" : "secondary"}
                        className={c.is_active ? "bg-emerald-600/80" : "bg-white/10 text-white/60"}
                        data-testid={`${PA.statusBadgePrefix}${c.id}`}
                      >
                        {c.is_active ? "Activa" : "Inactiva"}
                      </Badge>
                    </TableCell>
                    <TableCell>{c.counts.users}</TableCell>
                    <TableCell>{c.counts.services}</TableCell>
                    <TableCell>{c.counts.patients}</TableCell>
                    <TableCell>{c.counts.appointments}</TableCell>
                    <TableCell className="text-white/50 text-sm">{formatDate(c.created_at)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-white/10 text-white hover:bg-white/10"
                          disabled={busyId === c.id}
                          onClick={() => toggleStatus(c)}
                          data-testid={c.is_active ? `${PA.deactivateButtonPrefix}${c.id}` : `${PA.activateButtonPrefix}${c.id}`}
                        >
                          {c.is_active ? <Ban className="w-3.5 h-3.5 mr-1" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />}
                          {c.is_active ? "Desactivar" : "Reactivar"}
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => setDeleteTarget(c)}
                          data-testid={`${PA.deleteButtonPrefix}${c.id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5 mr-1" /> Eliminar
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}

        <div className="space-y-3" data-testid={PA.auditLogSection}>
          <h2 className="font-serif text-lg text-white/80">Registro de auditoría</h2>
          <div className="bg-[#262220] border border-white/10 rounded-xl divide-y divide-white/5 max-h-72 overflow-y-auto">
            {auditLog.length === 0 && <p className="text-sm text-white/40 p-4">Sin acciones registradas todavía.</p>}
            {auditLog.map((entry) => (
              <div key={entry.id} className="p-3 text-sm flex justify-between gap-4" data-testid={`${PA.auditLogItemPrefix}${entry.id}`}>
                <span>
                  <span className="text-white/40">{formatDate(entry.performed_at)}</span>{" "}
                  <strong className="uppercase text-xs tracking-wide text-[#C9B8A6]">{entry.action}</strong>{" "}
                  · {entry.tenant_name}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <DeleteClinicDialog
        clinic={deleteTarget}
        token={token}
        open={!!deleteTarget}
        onOpenChange={(v) => !v && setDeleteTarget(null)}
        onDeleted={load}
      />
    </div>
  );
}

export default function PlatformAdminPage() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_STORAGE_KEY) || null);

  const handleLogout = () => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken(null);
  };

  if (!token) return <TokenGate onConnected={setToken} />;
  return <ClinicsPanel token={token} onLogout={handleLogout} />;
}
